import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/movies";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const handleCategoryClick = (slug: string) => {
    navigate(`/category/${slug}`);
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1440px] mx-auto">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-2 group"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-film-line text-lg text-white"></i>
            </div>
            <span className="text-xl font-semibold text-foreground-950 whitespace-nowrap">
              青柠影院
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                isActive("/") && location.pathname === "/"
                  ? "text-primary-500 bg-primary-50"
                  : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
              }`}
            >
              首页
            </a>

            {/* Categories Dropdown */}
            <div className="relative group">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-500 bg-primary-50"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                }`}
              >
                电影分类
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              <div className="absolute top-full left-0 mt-1 bg-background-50 rounded-xl shadow-lg border border-background-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[180px] py-2">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryClick(cat.slug)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors flex items-center gap-2 whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <a
              href="/about"
              onClick={(e) => {
                e.preventDefault();
                navigate("/about");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                isActive("/about")
                  ? "text-primary-500 bg-primary-50"
                  : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
              }`}
            >
              关于我们
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors"
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3 space-y-1">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                isActive("/") && location.pathname === "/"
                  ? "text-primary-500 bg-primary-50"
                  : "text-foreground-700"
              }`}
            >
              首页
            </a>
            <div className="text-sm font-medium text-foreground-500 px-4 py-2">
              电影分类
            </div>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </button>
            ))}
            <a
              href="/about"
              onClick={(e) => {
                e.preventDefault();
                navigate("/about");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                isActive("/about")
                  ? "text-primary-500 bg-primary-50"
                  : "text-foreground-700"
              }`}
            >
              关于我们
            </a>
          </div>
        </div>
      )}
    </header>
  );
}